import React, { useState } from 'react';
import { Button, Modal, Input, Alert, Checkbox, Form, theme } from 'antd';
import { message } from '../services/toastMessage';
import { ExclamationCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { requestAccountDeletion } from '../services/AuthenticationService';
import { useAuth } from '../contextProviders/AuthProvider';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

const DeleteAccountSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [canDelete, setCanDelete] = useState(false);
  const [form] = Form.useForm();
  const { logout } = useAuth();

  const showModal = () => {
    setIsModalVisible(true);
    setCountdown(5);
    setCanDelete(false);

    // Countdown de seguridad
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setCanDelete(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleDelete = async (values: { password: string; reason?: string; confirmed: boolean }) => {
    if (!values.confirmed) {
      message.error(translate(t, TRANSLATIONS.DELETE_ACCOUNT_CONFIRM_REQUIRED_ERROR));
      return;
    }

    setLoading(true);
    try {
      const response = await requestAccountDeletion(values.password, values.reason);

      message.success({
        content: t(TRANSLATIONS.DELETE_ACCOUNT_SCHEDULED_MESSAGE, {
          date: new Date(response.deletionDate).toLocaleDateString(),
        }) as string,
        duration: 8,
      });

      setIsModalVisible(false);
      form.resetFields();

      // Auto-logout después de 3 segundos
      setTimeout(() => {
        logout();
      }, 3000);

    } catch (error: any) {
      const errorMessage = error.response?.data?.data || translate(t, TRANSLATIONS.DELETE_ACCOUNT_ERROR_FALLBACK);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        marginTop: 20,
        padding: 24,
        borderRadius: 20,
        border: '1px solid #FBDCD3',
        background: '#FEF6F3',
      }}
    >
      <h3 style={{ color: token.colorError, marginBottom: '16px' }}>
        <ExclamationCircleOutlined /> {translate(t, TRANSLATIONS.DELETE_ACCOUNT_DANGER_ZONE_TITLE)}
      </h3>

      <Alert
        message={translate(t, TRANSLATIONS.DELETE_ACCOUNT_ALERT_MESSAGE)}
        description={translate(t, TRANSLATIONS.DELETE_ACCOUNT_ALERT_DESCRIPTION)}
        type="warning"
        showIcon
        style={{ marginBottom: '16px' }}
      />

      <Button
        shape="round"
        danger
        icon={<DeleteOutlined />}
        onClick={showModal}
      >
        {translate(t, TRANSLATIONS.DELETE_ACCOUNT_BUTTON)}
      </Button>

      <Modal
        title={
          <span style={{ color: token.colorError }}>
            <ExclamationCircleOutlined /> {translate(t, TRANSLATIONS.DELETE_ACCOUNT_BUTTON)}
          </span>
        }
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        styles={{ content: { borderRadius: 20 } }}
      >
        <Alert
          message={translate(t, TRANSLATIONS.DELETE_ACCOUNT_MODAL_WARNING_MESSAGE)}
          description={
            <ul style={{ marginTop: '12px', paddingLeft: '20px' }}>
              <li>{translate(t, TRANSLATIONS.DELETE_ACCOUNT_MODAL_BULLET_1)}</li>
              <li>{translate(t, TRANSLATIONS.DELETE_ACCOUNT_MODAL_BULLET_2)}</li>
              <li>{translate(t, TRANSLATIONS.DELETE_ACCOUNT_MODAL_BULLET_3)}</li>
              <li>{translate(t, TRANSLATIONS.DELETE_ACCOUNT_MODAL_BULLET_4)}</li>
              <li>{translate(t, TRANSLATIONS.DELETE_ACCOUNT_MODAL_BULLET_5)}</li>
            </ul>
          }
          type="error"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleDelete}
        >
          <Form.Item
            label={translate(t, TRANSLATIONS.DELETE_ACCOUNT_PASSWORD_LABEL)}
            name="password"
            rules={[
              { required: true, message: translate(t, TRANSLATIONS.DELETE_ACCOUNT_PASSWORD_REQUIRED) }
            ]}
          >
            <Input.Password
              placeholder={translate(t, TRANSLATIONS.DELETE_ACCOUNT_PASSWORD_PLACEHOLDER)}
              size="large"
            />
          </Form.Item>

          <Form.Item
            label={translate(t, TRANSLATIONS.DELETE_ACCOUNT_REASON_LABEL)}
            name="reason"
          >
            <Input.TextArea
              placeholder={translate(t, TRANSLATIONS.DELETE_ACCOUNT_REASON_PLACEHOLDER)}
              rows={3}
              maxLength={500}
            />
          </Form.Item>

          <Form.Item
            name="confirmed"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value ? Promise.resolve() : Promise.reject(new Error(translate(t, TRANSLATIONS.DELETE_ACCOUNT_MUST_CONFIRM_ERROR))),
              },
            ]}
          >
            <Checkbox>
              {translate(t, TRANSLATIONS.DELETE_ACCOUNT_CHECKBOX_LABEL)}
            </Checkbox>
          </Form.Item>

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <Button shape="round" onClick={handleCancel}>
              {translate(t, TRANSLATIONS.DELETE_ACCOUNT_CANCEL_BUTTON)}
            </Button>
            <Button
              shape="round"
              type="primary"
              danger
              htmlType="submit"
              loading={loading}
              disabled={!canDelete}
            >
              {canDelete
                ? translate(t, TRANSLATIONS.DELETE_ACCOUNT_CONFIRM_BUTTON)
                : (t(TRANSLATIONS.DELETE_ACCOUNT_WAIT_BUTTON, { seconds: countdown }) as string)}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default DeleteAccountSection;
